import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import useElementHistoryController from "src/components/ElementHistory/useElementHistoryController";
import { useSeriesStore } from "src/stores/seriesStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { SudoButton } from "src/sudokit/components/SudoButton";
import ThreeDotsSelect from "src/sudokit/components/SudoSelect/ThreeDotsSelect";
import styled from "styled-components/macro";
import { FUNCTION_ICONS, FUNCTION_NAMES } from "../..";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../../../AppEventPublisher";
import Auth from "../../../../Auth";
import { useProjectStore } from "../../../../stores/projectStore";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { isStoryBibleCtaBlockingRetrigger } from "src/components/PersistentChat/storyBibleCta";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { useStableCallback } from "src/hooks/useStableCallback";
import ElementHistory from "../../../ElementHistory";
import ImportOutline from "../../../Modals/ImportOutline";
import StickyNoteAnchor from "../../../StickyTour/StickyNoteAnchor";
import { AnchorIds } from "../../../StickyTour/TourTypes";
import { useToast } from "../../../ToastWrapper";
import SeriesSelectorButton from "../../SeriesSelectorButton";
import SeriesTag from "../../SeriesTag";
import GenerateButton from "../GenerateButton";
import { useGenerateOutline } from "../GenerateButton/useGenerateOutline";
import { useStructuredOutlineStore } from "../StructuredOutlineStore";
import { DeleteOutlineConfirmDialog } from "./DeleteOutlineConfirmDialog";
import { POVTenseBar } from "./POVTenseBar";

const OutlineHeaderContainer = styled.div`
  border-radius: 22px;
  background-color: var(--simplified-sb-background);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px 22px 16px 8px;
  padding-bottom: 10px;
  cursor: ${(props) => (props.$collapsible ? "pointer" : "default")};
`;

const TopContainerLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  row-gap: 10px;

  h2 {
    font-size: 20px !important;
    font-weight: 500 !important;
    margin-bottom: 0 !important;
  }

  .chevron-wrapper {
    display: flex;
    align-items: center;
    margin-right: 4px;
  }

  .collapse-chevron {
    cursor: pointer;
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;

    path {
      fill: var(--grey-2) !important;
    }
  }

  .rotated-chevron {
    transform: rotate(-90deg);
  }

  svg {
    color: var(--grey-0) !important;
    path {
      fill: var(--grey-0) !important;
    }
  }
`;

const TopContainerRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  row-gap: 10px;
  gap: 7px;
  border-radius: 20px;
  .copy-icon {
    cursor: pointer;

    color: var(--grey-0) !important;
    path {
      fill: var(--grey-0) !important;
    }
  }
`;

function Header({ projectId, restoreOutline, onGenerate, onToggleCollapse, collapsed }) {
  const features = useSudowriteStore((state) => state.getFeatureFlags)();
  const { hasAccessToPOVAndTenseBeta } = features || {};
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const globalCtaActive = usePersistentChatStore((state) =>
    isStoryBibleCtaBlockingRetrigger(state.ctaAction),
  );

  const [importOpen, setImportOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const project = useProjectStore((state) => state.project);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const outline = useStructuredOutlineStore((state) => state.outline);
  const deleteOutline = useStructuredOutlineStore((state) => state.deleteOutline);
  const setRewriteOpen = useStructuredOutlineStore((state) => state.setRewriteOpen);
  const setShowGoToChat = useStructuredOutlineStore((state) => state.setShowGoToChat);
  const getPromptRepresentation = useStructuredOutlineStore((state) => state.getPromptRepresentation);
  const addChapter = useStructuredOutlineStore((state) => state.addChapter);
  const addBlankAct = useStructuredOutlineStore((state) => state.addBlankAct);
  const selectedSeriesProjectId = useSeriesStore((state) => state.selectedSeriesProjectId);
  const setAllItemsCollapsed = useStructuredOutlineStore((state) => state.setAllItemsCollapsed);
  const { setToast } = useToast();
  const generateOutlineIsPrimary = outline?.content?.items.length === 0;
  const { handleGenerate: triggerGenerate } = useGenerateOutline({ projectId, onGenerate });

  const allCollapsed = useMemo(() => {
    const items = outline?.content?.items || [];
    return items.length > 0 && items.every((item) => item.collapsed);
  }, [outline?.content?.items]);

  const handleCollapseExpandAll = useCallback(() => {
    setAllItemsCollapsed(!allCollapsed);
  }, [allCollapsed, setAllItemsCollapsed]);

  const elementHistoryController = useElementHistoryController({
    projectId,
    elementId: outline.id,
    sourceTable: "outlines",
    restore: restoreOutline,
    listLabel: "Outline History",
  });
  const { open: openElementHistory, isOpen: elementHistoryOpen } = elementHistoryController;
  // Ref and state to track series selector width
  const seriesSelectorRef = useRef(null);
  const [seriesSelectorWidth, setSeriesSelectorWidth] = useState(0);

  // Compute button visibility inline so it's always in sync with generateOutlineIsPrimary.
  // Using useMemo instead of useState+useEffect avoids a stale one-render cycle that
  // caused the Add Chapter button to flicker when chapters were deleted.
  const buttonVisibility = useMemo(() => {
    const centerWidth = columnWidths.center.current;
    if (!centerWidth) return { showAddChapter: true, showAddAct: true, showGenerate: true };

    const seriesSelectorOffset = seriesSelectorWidth;
    const generateButtonOffset = generateOutlineIsPrimary ? 165 : 0;

    const THRESHOLD_HIDE_ADD_ACT = 650 + seriesSelectorOffset + generateButtonOffset;
    const THRESHOLD_HIDE_ADD_CHAPTER = 560 + seriesSelectorOffset + generateButtonOffset;
    const THRESHOLD_HIDE_GENERATE = 280 + seriesSelectorOffset + generateButtonOffset;

    return {
      showAddChapter: centerWidth >= THRESHOLD_HIDE_ADD_CHAPTER,
      showAddAct: centerWidth >= THRESHOLD_HIDE_ADD_ACT,
      showGenerate: centerWidth >= THRESHOLD_HIDE_GENERATE,
    };
  }, [columnWidths.center, seriesSelectorWidth, generateOutlineIsPrimary]);

  const handleExport = async () => {
    try {
      const blob = await Auth.fetchFileWithIdToken("/api/export-outline", { projectId });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${project.title} Outline.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      AppEventPublisher.storyBible.action(StoryBibleType.Outline, StoryBibleAction.Export);
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "structured_outline_export" } });
      setToast("Could not export outline. Please try again.");
    }
  };

  // Measure series selector width dynamically
  useEffect(() => {
    if (!seriesSelectorRef.current) {
      setSeriesSelectorWidth(0);
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setSeriesSelectorWidth(width);
      }
    });

    resizeObserver.observe(seriesSelectorRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [project.seriesId, hasAccessToPOVAndTenseBeta, selectedSeriesProjectId]);

  const handleOutlineMenuChange = useStableCallback((option) => {
    switch (option) {
      case "Add Chapter":
        addChapter("beginning");
        break;
      case "Add Act":
        addBlankAct("beginning");
        break;
      case "Copy Outline":
        navigator.clipboard.writeText(getPromptRepresentation());
        setToast("Outline copied");
        AppEventPublisher.storyBible.copy(StoryBibleType.Outline, "menu");
        break;
      case "Outline History":
        openElementHistory();
        break;
      case "Expand All":
      case "Collapse All":
        handleCollapseExpandAll();
        break;
      case "Generate":
        triggerGenerate({ withToast: true });
        break;
      case "Rewrite Outline":
        if (hasAccessToAgenticChat) {
          if (!globalCtaActive) {
            emitLayoutEvent("trigger_cta_chat", {
              type: "rewrite_outline",
              displayTitle: "Rewrite Outline",
              userMessage: "Rewrite Outline",
              cannedResponse: "Ok, let's do it!",
            });
          }
        } else {
          setRewriteOpen(true);
          setShowGoToChat(false);
        }
        break;
      case "Import Outline":
        setImportOpen(true);
        break;
      case "Export Outline":
        handleExport();
        break;
      case "Delete Outline":
        setDeleteConfirmOpen(true);
        break;
      default:
        break;
    }
  });

  return (
    <OutlineHeaderContainer>
      <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_OUTLINE_HEADER} />
      <TopContainer>
        <TopContainerLeft
          className={onToggleCollapse ? "cursor-pointer" : ""}
          style={onToggleCollapse ? { userSelect: "none" } : undefined}
          onClick={(e) => {
            if (!onToggleCollapse) return;
            e.preventDefault();
            onToggleCollapse();
          }}
        >
          {onToggleCollapse && (
            <div className="chevron-wrapper">
              <SVG
                src="/assets/chevron-down.svg"
                className={classNames("collapse-chevron", { "rotated-chevron": collapsed })}
                aria-label={`${collapsed ? "Expand" : "Collapse"} Outline`}
              />
            </div>
          )}
          <SVG
            src={FUNCTION_ICONS[FUNCTION_NAMES.OUTLINE]}
            height={32}
            width={32}
          />
          <h2
            id="heading-outline"
            className="mb-0 text-[20px]"
          >
            Outline
          </h2>
        </TopContainerLeft>
        <TopContainerRight onClick={(e) => e.stopPropagation()}>
          {project.seriesId && hasAccessToPOVAndTenseBeta && (
            <div
              ref={seriesSelectorRef}
              className="ml-2.5 flex items-center gap-2 justify-end"
            >
              <SeriesTag />
              <SeriesSelectorButton type={FUNCTION_NAMES.OUTLINE} />
            </div>
          )}
          {elementHistoryOpen && (
            <ElementHistory
              elementId={outline.id}
              sourceTable="outlines"
              listLabel="Outline History"
              restore={restoreOutline}
              projectId={projectId}
              hoverTipText={"Outline History"}
              controller={elementHistoryController}
            />
          )}
          {buttonVisibility.showAddChapter && (
            <SudoButton
              variant="tertiary"
              className="mt-0"
              leftIcon={"/assets/add.svg"}
              onClick={() => addChapter("beginning")}
            >
              Add Chapter
            </SudoButton>
          )}
          {buttonVisibility.showAddAct && (
            <SudoButton
              variant="tertiary"
              className="mt-0"
              leftIcon={"/assets/add.svg"}
              onClick={() => addBlankAct("beginning")}
            >
              Add Act
            </SudoButton>
          )}
          {generateOutlineIsPrimary && buttonVisibility.showGenerate && (
            <GenerateButton
              projectId={projectId}
              onGenerate={onGenerate}
              primary
            />
          )}
          <ThreeDotsSelect
            options={[
              // Show Generate in menu when the button is hidden for space
              ...(generateOutlineIsPrimary && !buttonVisibility.showGenerate
                ? [{ value: "Generate", label: "Generate", icon: { src: "/assets/AI-sparkle.svg", label: "Generate", status: "default" } }]
                : []),
              ...(!buttonVisibility.showAddChapter ? [{ value: "Add Chapter", label: "Add Chapter", icon: { src: "/assets/add.svg", label: "Add Chapter", status: "default" } }] : []),
              ...(!buttonVisibility.showAddAct ? [{ value: "Add Act", label: "Add Act", icon: { src: "/assets/add.svg", label: "Add Act", status: "default" } }] : []),
              { value: "Copy Outline", label: "Copy Outline", icon: { src: "/assets/duplicate.svg", label: "Copy Outline", status: "default" } },
              { value: "Outline History", label: "Outline History", icon: { src: "/assets/clock.svg", label: "Outline History", status: "default" } },
              ...(outline?.content?.items?.length > 0
                ? [
                    {
                      value: allCollapsed ? "Expand All" : "Collapse All",
                      label: allCollapsed ? "Expand All" : "Collapse All",
                      icon: {
                        src: allCollapsed ? "/assets/expand-all.svg" : "/assets/collapse-all.svg",
                        label: allCollapsed ? "Expand All" : "Collapse All",
                        status: "default",
                      },
                    },
                  ]
                : []),
              // Standard menu options
              { value: "Rewrite Outline", label: "Rewrite", icon: { src: "/assets/rewriteIcon.svg", label: "Rewrite Outline", status: "default" } },
              { value: "Import Outline", label: "Import", icon: { src: "/assets/import-icon.svg", label: "Import Outline", status: "default" } },
              { value: "Export Outline", label: "Export", icon: { src: "/assets/export-icon.svg", label: "Export Outline", status: "default" } },
              { value: "Delete Outline", label: "Delete", icon: { src: "/assets/delete.svg", label: "Delete Outline", status: "danger" } },
            ]}
            onChange={handleOutlineMenuChange}
            optionAlignment="left"
            align="end"
            className='max-w-[40px]'
          />
        </TopContainerRight>
      </TopContainer>
      {hasAccessToPOVAndTenseBeta && !collapsed ? (
        <POVTenseBar
          projectId={projectId}
          setImportOpen={setImportOpen}
        />
      ) : (
        <div style={{ height: 16 }} />
      )}
      <ImportOutline
        isOpen={importOpen}
        setIsOpen={setImportOpen}
        projectId={projectId}
      />
      <DeleteOutlineConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmOpen(false);
        }}
        onConfirm={() => {
          deleteOutline();
          AppEventPublisher.storyBible.action(StoryBibleType.Outline, StoryBibleAction.Delete);
          setDeleteConfirmOpen(false);
        }}
      />
    </OutlineHeaderContainer>
  );
}

export default Header;
